import { GestureType, GestureEvent, AppState, DisplayMode, PersonID, VisualizationType, cycleVisualizationType } from '../../types';
import { cycleColor } from '../animation-engine/utils/ColorUtils';

/**
 * Trail length bounds
 */
const MIN_TRAIL_LENGTH = 10;
const MAX_TRAIL_LENGTH = 60;

/**
 * GestureActionMapper - Maps gesture events to state changes
 * Validates Requirements 3.4.3, 4.2, 4.5
 */
export class GestureActionMapper {
  /**
   * Apply gesture action to state
   * @param gesture Gesture event to process
   * @param currentState Current application state
   * @param personId Optional person identifier for multi-person mode
   * @returns Partial state update to apply
   */
  static applyGestureAction(
    gesture: GestureEvent,
    currentState: AppState,
    personId?: PersonID
  ): Partial<AppState> {
    switch (gesture.type) {
      case GestureType.ARMS_RAISED:
        return this.handleArmsRaised(currentState, personId);
      
      case GestureType.ARMS_CROSSED:
        return this.handleArmsCrossed(currentState);
      
      case GestureType.LEFT_ARM_EXTENDED:
        return this.handleLeftArmExtended(currentState, personId);
      
      case GestureType.RIGHT_ARM_EXTENDED:
        return this.handleRightArmExtended(currentState, personId);
      
      case GestureType.RIGHT_ARM_RAISED:
        // Cycle this person's visualization forward (next style)
        return this.handleCycleVisualization(currentState, 1, personId);
      
      case GestureType.LEFT_ARM_RAISED:
        // Cycle this person's visualization backward (previous style)
        return this.handleCycleVisualization(currentState, -1, personId);
      
      default:
        return {};
    }
  }

  /**
   * Handle ARMS_RAISED gesture - cycle colors
   * In multi-person mode, only affects the specific person's colors
   * In single-person mode, affects the global visual config
   */
  private static handleArmsRaised(currentState: AppState, personId?: PersonID): Partial<AppState> {
    const newColor = cycleColor();
    
    // Multi-person mode: update specific person's colors
    if (personId) {
      const person = currentState.people.people.get(personId);
      if (!person) {
        return {}; // Person not found, no update
      }
      
      const updatedPerson = {
        ...person,
        visualConfig: {
          ...person.visualConfig,
          primaryColor: newColor,
          secondaryColor: newColor,
        }
      };
      
      const updatedPeople = new Map(currentState.people.people);
      updatedPeople.set(personId, updatedPerson);
      
      return {
        people: {
          people: updatedPeople,
          frameCount: currentState.people.frameCount
        }
      };
    }
    
    // Single-person mode: update global visual config
    return {
      visualConfig: {
        ...currentState.visualConfig,
        primaryColor: newColor,
        secondaryColor: newColor,
      },
    };
  }

  /**
   * Cycle a person's visualization type forward or backward.
   * Triggered by single-arm-raised gestures (RIGHT = next, LEFT = previous).
   * Only affects the specific person in multi-person mode; no-op without a personId
   * (the visualization type is a per-person property).
   *
   * @param currentState Current application state
   * @param direction +1 for next visualization, -1 for previous
   * @param personId Person whose visualization should change
   */
  private static handleCycleVisualization(
    currentState: AppState,
    direction: 1 | -1,
    personId?: PersonID
  ): Partial<AppState> {
    if (!personId) {
      return {};
    }

    const person = currentState.people.people.get(personId);
    if (!person) {
      return {};
    }

    const nextType = cycleVisualizationType(person.visualizationType, direction);
    return this.setVisualizationType(currentState, personId, nextType);
  }

  /**
   * Set a specific person's visualization type to an explicit value.
   * Used by the on-screen manual selector. Returns an immutable people update.
   *
   * @param currentState Current application state
   * @param personId Person to update
   * @param type The visualization type to assign
   */
  static setVisualizationType(
    currentState: AppState,
    personId: PersonID,
    type: VisualizationType
  ): Partial<AppState> {
    const person = currentState.people.people.get(personId);
    if (!person) {
      return {};
    }

    const updatedPerson = {
      ...person,
      visualizationType: type,
    };

    const updatedPeople = new Map(currentState.people.people);
    updatedPeople.set(personId, updatedPerson);

    return {
      people: {
        people: updatedPeople,
        frameCount: currentState.people.frameCount,
      },
    };
  }

  /**
   * Handle ARMS_CROSSED gesture - toggle display mode
   * This gesture affects all people globally
   */
  private static handleArmsCrossed(currentState: AppState): Partial<AppState> {
    const newMode = currentState.displayMode === DisplayMode.ARTISTIC
      ? DisplayMode.POSE_ANALYSIS
      : DisplayMode.ARTISTIC;
    
    return {
      displayMode: newMode,
    };
  }

  /**
   * Handle LEFT_ARM_EXTENDED gesture - decrease trail length
   * In multi-person mode, only affects the specific person's trail length
   * In single-person mode, affects the global visual config
   */
  private static handleLeftArmExtended(currentState: AppState, personId?: PersonID): Partial<AppState> {
    // Multi-person mode: update specific person's trail length
    if (personId) {
      const person = currentState.people.people.get(personId);
      if (!person) {
        return {}; // Person not found, no update
      }
      
      const currentLength = person.visualConfig.trailLength;
      const newLength = Math.max(MIN_TRAIL_LENGTH, currentLength - 5);
      
      const updatedPerson = {
        ...person,
        visualConfig: {
          ...person.visualConfig,
          trailLength: newLength,
        }
      };
      
      const updatedPeople = new Map(currentState.people.people);
      updatedPeople.set(personId, updatedPerson);
      
      return {
        people: {
          people: updatedPeople,
          frameCount: currentState.people.frameCount
        }
      };
    }
    
    // Single-person mode: update global visual config
    const currentLength = currentState.visualConfig.trailLength;
    const newLength = Math.max(MIN_TRAIL_LENGTH, currentLength - 5);
    
    return {
      visualConfig: {
        ...currentState.visualConfig,
        trailLength: newLength,
      },
    };
  }

  /**
   * Handle RIGHT_ARM_EXTENDED gesture - increase trail length
   * In multi-person mode, only affects the specific person's trail length
   * In single-person mode, affects the global visual config
   */
  private static handleRightArmExtended(currentState: AppState, personId?: PersonID): Partial<AppState> {
    // Multi-person mode: update specific person's trail length
    if (personId) {
      const person = currentState.people.people.get(personId);
      if (!person) {
        return {}; // Person not found, no update
      }
      
      const currentLength = person.visualConfig.trailLength;
      const newLength = Math.min(MAX_TRAIL_LENGTH, currentLength + 5);
      
      const updatedPerson = {
        ...person,
        visualConfig: {
          ...person.visualConfig,
          trailLength: newLength,
        }
      };
      
      const updatedPeople = new Map(currentState.people.people);
      updatedPeople.set(personId, updatedPerson);
      
      return {
        people: {
          people: updatedPeople,
          frameCount: currentState.people.frameCount
        }
      };
    }
    
    // Single-person mode: update global visual config
    const currentLength = currentState.visualConfig.trailLength;
    const newLength = Math.min(MAX_TRAIL_LENGTH, currentLength + 5);
    
    return {
      visualConfig: {
        ...currentState.visualConfig,
        trailLength: newLength,
      },
    };
  }
}
